import { Fragment, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button, Menu } from "@mantine/core";

import { IconChevronDown } from "@tabler/icons-react";

import { CreateEncounterContext } from "@/contexts/create-encounter";
import {
  PostpartumClientQuestionnaireType,
  PostpartumFlowsheetMenuButton,
  PostpartumNewbornQuestionnaireType,
} from "@/encounters/components/EncounterLabor/constants/postpartum-flowsheets";
import { EncounterLaborQuickButtonDrawer } from "@/encounters/components/EncounterLabor/EncounterLaborDrawer/EncounterLaborQuickButtonDrawer";

export interface PostpartumMenuButtonProps {
  encounterId: string;
  clientId: string;
  flowSheetButtons: PostpartumFlowsheetMenuButton[];
  onRefresh?: () => Promise<void>;
}

export default function PostpartumMenuButton({
  encounterId,
  clientId,
  flowSheetButtons,
  onRefresh,
}: PostpartumMenuButtonProps) {
  const [opened, setOpened] = useState(false);
  const [selectedFlow, setSelectedFlow] = useState<PostpartumFlowsheetMenuButton | null>(null);
  const navigate = useNavigate();
  const { setNewbornIPPEncounterInitialValuesAndOpenDrawer } = useContext(CreateEncounterContext);

  const handleMenuClick = (item: PostpartumFlowsheetMenuButton) => {
    if (item.questionnaireName === PostpartumNewbornQuestionnaireType.NewbornPhysicalExam) {
      setNewbornIPPEncounterInitialValuesAndOpenDrawer();
      return;
    }

    if (item.questionnaireName === PostpartumClientQuestionnaireType.ClientFullIppAssessment) {
      navigate(`/clients/${clientId}/encounters/${encounterId}/client-ipp-assessment`);
    } else if (
      item.questionnaireName === PostpartumNewbornQuestionnaireType.NewbornFullAssessment
    ) {
      navigate(`/clients/${clientId}/encounters/${encounterId}/newborn-ipp-assessment`);
    } else {
      setSelectedFlow(item);
      setOpened(true);
    }
  };

  const handleClose = () => {
    setOpened(false);
    setSelectedFlow(null);
  };

  return (
    <>
      <Menu data-testid="postpartum-menu-button">
        <Menu.Target>
          <Button
            color="burgundy"
            variant="outline"
            data-testid="postpartum-menu-button"
            rightSection={<IconChevronDown size={14} />}
          >
            Add to Flowsheet
          </Button>
        </Menu.Target>
        <Menu.Dropdown>
          {flowSheetButtons.map((item, index) => (
            <Fragment key={item.questionnaireName}>
              <Menu.Item
                onClick={() => handleMenuClick(item)}
                data-testid={`postpartum-menu-item-${item.questionnaireName}`}
              >
                {item.displayTitle}
              </Menu.Item>
              {index === 0 && <Menu.Divider />}
            </Fragment>
          ))}
        </Menu.Dropdown>
      </Menu>

      {selectedFlow && (
        <EncounterLaborQuickButtonDrawer
          opened={opened}
          clientId={clientId}
          onClose={handleClose}
          onRefresh={onRefresh}
          encounterId={encounterId}
          flowsheetContext="postpartum"
          questionnaireTitle={selectedFlow.displayTitle}
          questionnaireName={selectedFlow.questionnaireName}
        />
      )}
    </>
  );
}
